import {
    IconButton, AppBar, Toolbar, Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import PTMTooltipEllipsis from "./PTMTooltipEllipsis";
// Todo: Später prüfen
// eslint-disable-next-line import/no-cycle
import HelpDialog from "../Main/HelpDialog/HelpDialog";
import useStore from "../store";
import api from "../api";
// eslint-disable-next-line import/no-cycle
import { PTMInput, PTMTooltip } from "./index";
import PTMMenuButton from "./PTMMenuButton";

const sizeStyles = {
    sm: {
        "& span, button": {
            fontSize: 12,
        },
        "& svg": {
            fontSize: 14,
        },
        "& .MuiButton-root svg": {
            marginRight: 5,
            fontSize: 12,
        },
    },
    md: {
        "& .MuiToolbar-root": {
            height: 30,
        },
        "& span, button": {
            fontSize: 14,
        },
        "& svg": {
            fontSize: 16,
        },
        "& .MuiIconButton-root": {
            width: 30,
        },
        "& .MuiButton-root": {
            height: 30,
        },
        "& .MuiButton-root svg": {
            marginRight: 5,
            fontSize: 14,
        },
    },
    lg: {
        "& .MuiToolbar-root": {
            height: 35,
        },
        "& span": {
            fontSize: 20,
        },
        "& svg": {
            fontSize: 22,
        },
        "& .MuiIconButton-root": {
            width: 35,
        },
        "& .MuiButton-root": {
            height: 35,
        },
        "& .MuiButton-root svg": {
            marginRight: 5,
            fontSize: 20,
        },
    },
};

const StyledAppBar = styled(AppBar, {
    shouldForwardProp: (prop) => !["noMoveCursor"].includes(prop),
})(({
    noMoveCursor,
}) => ({
    overflow: "hidden",
    "& span": {
        fontSize: "12px",
        marginLeft: 5,
        marginRight: 5,
        overflow: "hidden",
        textOverflow: "ellipsis",
        paddingTop: "0px",
    },
    "& .MuiButton-root": {
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 0,
    },
    "& .MuiToolbar-root": {
        padding: "0px",
    },
    "& .rgl-drag-handle": {
        cursor: "move",
    },
    "& .drag-handle": {
        cursor: "move",
    },

    ...noMoveCursor && {
        "& .rgl-drag-handle": {
            cursor: "initial",
        },
        "& .drag-handle": {
            cursor: "initial",
        },
    },
}));

/**
 * AppBar for Widget oder GridLayout Components
 * New Feature: When prop "clippableTitle" prop are passed to this component, user can click on the title of
 * the AppBar and the string passed in the prop will be copied in clipboard.
 * For http (insecure) websites: use document.execCommand('copy') (depecrated, but apparently the only alternative atm)
 */
const PTMAppBar = React.memo((props) => {
    const {
        icon,
        icons,
        title,
        actionButtons,
        controlButtons,
        menuButtons,
        active,
        helpName,
        draggable,
        dragHandle,
        color,
        foregroundColor,
        style,
        disabledControls,
        size,
        clippableTitle,
        noMoveCursor,
        searchBar,
        disableEllipsis,
        clickableTitle,
        onTitleClick,
        titleTooltip,
        centerTitle,
    } = props;

    const [showHelp, setShowHelp] = useState(false);
    const [helpData, setHelpData] = useState();
    const [tooltipText, setTooltipText] = useState("Im Clipboard kopieren");
    const [searchString, setSearchString] = useState("");

    const canEditHelp = useStore((state) => state.app.rights?.help?.rights.manage);

    const clearSearch = () => {
        setSearchString("");
    };

    const getHelpData = async () => {
        const data = await api.help.find(helpName);
        setHelpData(data.data);
    };
    const copyTitle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setTooltipText("Kopiert");

        setTimeout(() => {
            setTooltipText("Im Clipboard kopieren");
        }, 1000);

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(clippableTitle);
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = clippableTitle;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            return new Promise((res, rej) => {
                // eslint-disable-next-line no-unused-expressions
                document.execCommand("copy") ? res() : rej();
                textArea.remove();
            });
        }
    };
    useEffect(() => {
        if (helpName) {
            getHelpData();
        }
    }, []);
    return (
        <StyledAppBar
            position="static"
            sx={{ bgcolor: active ? "primary.dark" : color, ...style, ...sizeStyles[size] }}
            noMoveCursor={noMoveCursor}
        >
            {showHelp && (
                <HelpDialog
                    isOpen
                    onClose={() => setShowHelp(false)}
                    helpInfo={helpData}
                    canEdit={canEditHelp}
                />
            )}
            <Toolbar className={draggable ? (dragHandle ? `drag-handle ${dragHandle}` : "rgl-drag-handle") : null}>
                {controlButtons.filter((button) => button.position === "left").map((button) => (
                    !button.hide && (
                        <>
                            {button.component && (
                                button.component
                            )}
                            {button.displayTitle && (
                                button.tooltip ? (
                                    <PTMTooltip title={button.tooltip} key={button.title}>
                                        <Button
                                            className="noDrag"
                                            variant="contained"
                                            key={button.title}
                                            disabled={button.disabled || disabledControls}
                                            color={button.color}
                                            onMouseDown={button.click}
                                            sx={{ color: foregroundColor || "" }}
                                            ref={button.ref}
                                        >
                                            {button.icon && (<FontAwesomeIcon icon={button.icon} />)}
                                            {" "}
                                            {button.title}
                                        </Button>
                                    </PTMTooltip>
                                ) : (
                                    <Button
                                        variant="contained"
                                        className="noDrag"
                                        key={button.title}
                                        disabled={button.disabled || disabledControls}
                                        color={button.color}
                                        onMouseDown={button.click}
                                        sx={{ color: foregroundColor || "" }}
                                        ref={button.ref}
                                    >
                                        {button.icon && (<FontAwesomeIcon icon={button.icon} />)}
                                        {" "}
                                        {button.title}
                                    </Button>
                                )
                            )}
                            {!button.component && !button.displayTitle && !button.asButton && (
                                <PTMTooltip
                                    title={button.title}
                                    // key={React.useMemo(()=>button.title, [])}
                                    key={button.key}
                                >
                                    <IconButton
                                        className="noDrag"
                                        disabled={button.disabled || disabledControls}
                                        color={button.color}
                                        onMouseDown={button.click}
                                        sx={{ color: foregroundColor || "", ...button.sx }}
                                        ref={button.ref}
                                    >
                                        {button.icon && (<FontAwesomeIcon icon={button.icon} />)}
                                    </IconButton>
                                </PTMTooltip>
                            )}
                            {!button.component && !button.displayTitle && button.asButton && (
                                <PTMTooltip
                                    title={button.title}
                                    // key={React.useMemo(()=>button.title, [])}
                                    key={button.key}
                                >
                                    <Button
                                        variant="contained"
                                        className="noDrag"
                                        disabled={button.disabled || disabledControls}
                                        color={button.color}
                                        onMouseDown={button.click}
                                        sx={{ ...button.sx }}
                                        ref={button.ref}
                                    >
                                        {button.icon && (<FontAwesomeIcon icon={button.icon} style={{ margin: "0px" }} />)}
                                    </Button>
                                </PTMTooltip>
                            )}
                        </>
                    )
                ))}
                {actionButtons.position === "left" && actionButtons.buttons.map((button) => (
                    !button.hide && (
                        <PTMTooltip title={button.title} key={button.key || button.title}>
                            <Button
                                variant="contained"
                                disabled={button.disabled}
                                color={button.color}
                                onMouseDown={button.click}
                                className="appbar-button noDrag"
                                ref={button.ref}
                                sx={{
                                    "&.MuiButton-root svg": {
                                        marginRight: "0px !important",
                                    },
                                    ...button.sx,
                                }}
                            >
                                {button.icon && (<FontAwesomeIcon icon={button.icon} spin={button.spin} />)}
                            </Button>
                        </PTMTooltip>
                    )
                ))}
                <PTMTooltipEllipsis disabled={!!clippableTitle || disableEllipsis} style={{ flexGrow: 1, textAlign: centerTitle && "center" }}>
                    <span>
                        {icon && (
                            <FontAwesomeIcon
                                icon={icon.icon ? icon.icon : icon}
                                rotation={icon.rotation ? icon.rotation : null}
                                spin={icon.spin ? icon.spin : null}
                                style={{ color: foregroundColor || "", marginRight: 5 }}
                            />
                        )}
                        {icons && icons.map((iconItem) => (
                            <FontAwesomeIcon
                                key={iconItem.icon ? iconItem.icon : iconItem}
                                icon={iconItem.icon ? iconItem.icon : iconItem}
                                rotation={iconItem.rotation ? iconItem.rotation : null}
                                spin={iconItem.spin ? iconItem.spin : null}
                                style={{ color: foregroundColor || "", marginRight: 5 }}
                            />
                        ))}
                        {clippableTitle || clickableTitle
                            ? (
                                <PTMTooltip
                                    title={titleTooltip || tooltipText}
                                    sx={{ margin: "0!important" }}
                                >
                                    <Button
                                        variant="contained"
                                        style={{ backgroundColor: "transparent" }}
                                        onMouseDown={clickableTitle ? onTitleClick : copyTitle}
                                    >
                                        <span style={{ margin: 0, color: foregroundColor }}>
                                            {title}
                                        </span>
                                    </Button>
                                </PTMTooltip>
                            ) : (
                                <span style={{ margin: 0, color: foregroundColor }}>{title}</span>
                            )}
                    </span>
                </PTMTooltipEllipsis>
                {helpName && (
                    <PTMTooltip sx={{ cursor: "help" }} title="Hilfe">
                        <IconButton
                            color="inherit"
                            sx={{ color: foregroundColor || "" }}
                            // disabled={helpData?.is_published !== 1 && !canEditHelp}
                            onMouseDown={() => setShowHelp(true)}
                            className="noDrag"
                        >
                            <FontAwesomeIcon icon="question-circle" />
                        </IconButton>
                    </PTMTooltip>
                )}
                {searchBar
                    && (
                        <PTMInput
                            noForm
                            placeholder={`🔎 ${searchBar.placeholder}`}
                            onChange={(e) => {
                                searchBar.action(e);
                                setSearchString(e.target.value);
                            }}
                            disabled={searchBar.disabled}
                            value={searchString}
                            className="noDrag"
                            defaultValue=""
                            sx={
                                {
                                    flex: 1,
                                    backgroundColor: "#ffff",
                                    "& input": {
                                        color: "black",
                                        marginRight: "2px",
                                        fontSize: "13px",
                                        height: "85% !important",
                                    },
                                    "& button": {
                                        width: "22px",
                                    },
                                    "& fieldset": {
                                        display: "none !important",
                                    },
                                }
                            }
                            endAdornment={searchString && (
                                <IconButton
                                    disabled={searchBar.disabled}
                                    onClick={() => {
                                        clearSearch();
                                        searchBar.action({ target: { value: "" } });
                                    }}
                                >
                                    <FontAwesomeIcon icon="times" />
                                </IconButton>
                            )}
                        />
                    )}
                {actionButtons.position === "right" && actionButtons.buttons.map((button) => (
                    !button.hide && (
                        <PTMTooltip
                            title={button.title}
                            // key={React.useMemo(()=>button.title, [])}
                            key={button.key}
                        >
                            <Button
                                variant="contained"
                                disabled={button.disabled}
                                color={button.color}
                                onMouseDown={button.click}
                                className={["appbar-button-right", "noDrag"]}
                                ref={button.ref}
                                sx={{
                                    "&.MuiButton-root svg": {
                                        marginRight: "0px !important",
                                    },
                                    color: button.textColor,
                                }}
                            >
                                {button.icon && (<FontAwesomeIcon icon={button.icon} spin={button.spin} />)}
                            </Button>
                        </PTMTooltip>
                    )
                ))}
                {menuButtons?.map((button) => (
                    !button.hide && (
                        <PTMMenuButton
                            title={button.title}
                            key={button.title}
                            menuItems={button.menuItems}
                            color={button.color}
                            icon={button.icon}
                        />
                    )
                ))}
                {controlButtons.filter((button) => button.position !== "left").map((button) => (
                    !button.hide && (
                        <>
                            {button.component && (
                                button.component
                            )}
                            {button.displayTitle && (
                                button.tooltip ? (
                                    <PTMTooltip title={button.tooltip} key={button.title}>
                                        <Button
                                            className="noDrag"
                                            variant="contained"
                                            key={button.title}
                                            disabled={button.disabled || disabledControls}
                                            color={button.color}
                                            onMouseDown={button.click}
                                            sx={{ color: foregroundColor || "" }}
                                            ref={button.ref}
                                        >
                                            {button.icon && (<FontAwesomeIcon icon={button.icon} />)}
                                            {" "}
                                            {button.title}
                                        </Button>
                                    </PTMTooltip>
                                ) : (
                                    <Button
                                        variant="contained"
                                        className="noDrag"
                                        key={button.title}
                                        disabled={button.disabled || disabledControls}
                                        color={button.color}
                                        onMouseDown={button.click}
                                        sx={{ color: foregroundColor || "" }}
                                        ref={button.ref}
                                    >
                                        {button.icon && (<FontAwesomeIcon icon={button.icon} />)}
                                        {" "}
                                        {button.title}
                                    </Button>
                                )
                            )}
                            {!button.component && !button.displayTitle && !button.asButton && (
                                <PTMTooltip
                                    title={button.title}
                                    // key={React.useMemo(()=>button.title, [])}
                                    key={button.key}
                                >
                                    <IconButton
                                        className="noDrag"
                                        disabled={button.disabled || disabledControls}
                                        color={button.color}
                                        onMouseDown={button.click}
                                        sx={{ color: foregroundColor || "", ...button.sx }}
                                        ref={button.ref}
                                    >
                                        {button.icon && (<FontAwesomeIcon icon={button.icon} />)}
                                    </IconButton>
                                </PTMTooltip>
                            )}
                            {!button.component && !button.displayTitle && button.asButton && (
                                <PTMTooltip
                                    title={button.title}
                                    // key={React.useMemo(()=>button.title, [])}
                                    key={button.key}
                                >
                                    <Button
                                        variant="contained"
                                        className="noDrag"
                                        disabled={button.disabled || disabledControls}
                                        color={button.color}
                                        onMouseDown={button.click}
                                        sx={{ ...button.sx }}
                                        ref={button.ref}
                                    >
                                        {button.icon && (<FontAwesomeIcon icon={button.icon} style={{ margin: "0px" }} />)}
                                    </Button>
                                </PTMTooltip>
                            )}
                        </>
                    )
                ))}
            </Toolbar>
        </StyledAppBar>
    );
});

PTMAppBar.propTypes = {
    /**
     * FontAwesome Icon - free-solid-svg-icons
     */
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    /**
     * Title
     */
    title: PropTypes.string.isRequired,
    /**
     * Array of Button Settings Color Options: primary, secondary, error, warning, success, info
     */
    // TODO: fix
    // eslint-disable-next-line react/forbid-prop-types
    controlButtons: PropTypes.arrayOf(PropTypes.object),
    /**
     * Array of Button Settings Color Options: primary, secondary, error, warning, success, info
     */
    // TODO: fix
    // eslint-disable-next-line react/forbid-prop-types
    actionButtons: PropTypes.object,
    /**
     * active -> sligthly darker AppBar color
     */
    active: PropTypes.bool,
    /**
     * allocation string for helpsystem, example: task-info, task-activities, contact-addresses
     */
    helpName: PropTypes.string,
    /**
     * enables/disables dragging of the component
     */
    draggable: PropTypes.bool,
    /**
     * Size: sm = height: 25 | md = height: 30 | lg = height: 35
     */
    size: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types,react/require-default-props
    isClippable: PropTypes.string,
};

PTMAppBar.defaultProps = {
    controlButtons: [],
    actionButtons: [],
    active: false,
    helpName: null,
    icon: "",
    draggable: true,
    size: "sm",
};

export default PTMAppBar;
